// Force Shout (owner 2026-11-02: 'a shout sends a cone of rippling invisible force (heat-haze/distortion rings, dust ripples on
// the ground) that hurls enemies far back and briefly stuns them'). Render-only: reacts to the sim's 'shout' events (the sim's
// wave front moves at the same speed, SHOUT_SPEED) and decides nothing about gameplay.
//  - the force itself is (almost) invisible: a curved wall of heat haze at the wave front that refracts whatever is behind it
//    (the scene colour is sampled through screen-space ripples: viewportSharedTexture), with only a faint pale rim
//  - on the ground a train of ripples rolls out behind the front, warping the grass and kicking up a dust skirt and pebbles
//  - at the hero: a short burst of white speed lines into the cone and a tight shock ring; the hands-forward push pose
// Pooled: a few wave slots, each a wall mesh + a ground mesh with their own uniforms (hidden when idle, so the framebuffer copy
// only happens while a shout is on screen). No per-frame allocations.
import * as THREE from 'three/webgpu';
import { abs, atan, float, length, mod, positionLocal, screenUV, sin, cos, smoothstep, uniform, vec2, vec3, viewportSharedTexture } from 'three/tsl';
import type { SimEvent } from '../core/sim';
import type { Fx } from './fx';
import type { GroundGlow } from './groundGlow';
import { ACTIVE_LANCE, LANCE_POSES } from './heroPoses';
import type { HeroRig } from './models';
import { RibbonStrips, RS_STRIDE } from './ribbonStrips';

const SLOTS = 4, WALL_H = 2.4, FADE = 0.22, LINES = 28, LINE_LIFE = 0.26;
const PALE = 0xdcefff, DUST = 0xbfa888;
type ShoutEv = Extract<SimEvent, { t: 'shout' }>;

interface Slot {
  on: boolean; x: number; z: number; gy: number; a: number; half: number; r: number; speed: number; t: number; evo: boolean;
  wall: THREE.Mesh; ground: THREE.Mesh;
  u: { aim: ReturnType<typeof uniform>; half: ReturnType<typeof uniform>; alpha: ReturnType<typeof uniform>; front: ReturnType<typeof uniform>; R: ReturnType<typeof uniform>; time: ReturnType<typeof uniform> };
}
interface Line { on: boolean; x: number; y: number; z: number; dx: number; dz: number; len: number; sp: number; t: number }
const _edge = new THREE.Color(PALE).multiplyScalar(1.5), _core = new THREE.Color(1.8, 1.9, 2.0);

export class ForceShoutFx {
  /** 0..1 hands-forward push weight (view.ts applies it) and the world yaw of the shout */
  pose = 0; aimYaw = 0;
  /** QA counters / hold (freeze every wave at this age, s) */
  shouts = 0; hold: number | null = null;
  private slots: Slot[] = [];
  private lines: Line[] = [];
  private ribbons: RibbonStrips;
  private poseT = 0;

  constructor(scene: THREE.Scene, private groundAt: (x: number, z: number) => number) {
    this.ribbons = new RibbonStrips(scene, LINES, 3, true, 6);
    for (let i = 0; i < LINES; i++) this.lines.push({ on: false, x: 0, y: 0, z: 0, dx: 1, dz: 0, len: 1, sp: 1, t: 9 });
    const wallGeo = new THREE.CylinderGeometry(1, 1, 1, 96, 6, true); wallGeo.translate(0, 0.5, 0);
    const groundGeo = new THREE.CircleGeometry(1, 96, 0, Math.PI * 2); groundGeo.rotateX(-Math.PI / 2);
    // CircleGeometry has one ring of triangles: subdivide radially so the ripple warp has vertices to ride on? (the warp is
    // per pixel, so one ring is enough)
    for (let i = 0; i < SLOTS; i++) {
      const u = { aim: uniform(0), half: uniform(0.6), alpha: uniform(0), front: uniform(1), R: uniform(1), time: uniform(0) };
      const ang = atan(positionLocal.z, positionLocal.x);
      const da = abs(mod(ang.sub(u.aim).add(Math.PI), Math.PI * 2).sub(Math.PI));
      const cone = smoothstep(u.half.add(0.06), u.half.sub(0.22), da).max(smoothstep(3.0, 3.1, u.half)); // full circle when half ≈ π
      // --- wall of heat haze at the front ---
      const y = positionLocal.y;
      const vfade = smoothstep(0.0, 0.18, y).mul(smoothstep(1.0, 0.35, y));
      const w1 = sin(y.mul(17).sub(u.time.mul(34)).add(ang.mul(9))), w2 = cos(y.mul(9).add(u.time.mul(21)).sub(ang.mul(5)));
      const k = cone.mul(vfade).mul(u.alpha);
      const off = vec2(w1, w2).mul(0.028).mul(k);
      const wm = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide, fog: false });
      const sceneW = viewportSharedTexture(screenUV.add(off)).rgb;
      const rimW = smoothstep(0.6, 1.0, w1.mul(0.5).add(0.5)).mul(0.22).add(smoothstep(0.82, 1.0, y.oneMinus()).mul(0.1));
      wm.colorNode = sceneW.mul(float(1).add(k.mul(0.1))).add(vec3(0.78, 0.9, 1.0).mul(rimW).mul(k));
      wm.opacityNode = k.mul(0.95);
      const wall = new THREE.Mesh(wallGeo, wm); wall.visible = false; wall.frustumCulled = false; wall.renderOrder = 7;
      // --- ripples rolling over the ground behind the front ---
      const rr = length(positionLocal.xz).mul(u.R), behind = u.front.sub(rr);
      const band = smoothstep(-0.25, 0.1, behind).mul(smoothstep(2.8, 0.7, behind));
      const rip = sin(behind.mul(7.5).sub(u.time.mul(6)));
      const kg = band.mul(cone).mul(u.alpha);
      const dir = vec2(cos(ang), sin(ang).negate());
      const offG = dir.mul(rip.mul(0.024)).mul(kg);
      const gm = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, fog: false, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -4 });
      const sceneG = viewportSharedTexture(screenUV.add(offG)).rgb;
      const lead = smoothstep(0.3, 0.0, abs(behind)).mul(cone).mul(u.alpha);
      const crest = smoothstep(0.55, 0.95, rip).mul(kg); // pale crests: the ripples read as rings of rolling force
      gm.colorNode = sceneG.mul(float(1).add(rip.mul(0.12).mul(kg))).add(vec3(0.75, 0.66, 0.52).mul(kg.mul(0.1))).add(vec3(0.85, 0.93, 1.0).mul(lead.mul(0.35).add(crest.mul(0.22))));
      gm.opacityNode = kg.max(lead.mul(0.8)).min(1);
      const ground = new THREE.Mesh(groundGeo, gm); ground.visible = false; ground.frustumCulled = false; ground.renderOrder = 2;
      scene.add(wall, ground);
      this.slots.push({ on: false, x: 0, z: 0, gy: 0, a: 0, half: 0.6, r: 1, speed: 30, t: 9, evo: false, wall, ground, u });
    }
  }

  event(ev: SimEvent, fx: Fx, glow: GroundGlow): void {
    if (ev.t !== 'shout') return;
    this.start(ev, fx, glow);
  }

  private start(ev: ShoutEv, fx: Fx, glow: GroundGlow): void {
    this.shouts++;
    const s = this.slots.reduce((a, b) => (!b.on ? (a.on ? b : a) : !a.on ? a : b.t > a.t ? b : a));
    const gy = this.groundAt(ev.x, ev.z);
    Object.assign(s, { on: true, x: ev.x, z: ev.z, gy, a: Math.atan2(ev.dz, ev.dx), half: ev.half, r: ev.r, speed: ev.speed, t: 0, evo: ev.evolved });
    this.aimYaw = Math.atan2(ev.dx, ev.dz); this.poseT = 0.38;
    // speed lines bursting into the cone
    const full = ev.half >= Math.PI - 1e-3, n = ev.echo ? 8 : 14;
    for (let k = 0; k < n; k++) {
      const l = this.lines.reduce((a, b) => (!b.on ? (a.on ? b : a) : !a.on ? a : b.t > a.t ? b : a));
      const a = s.a + (full ? Math.random() * Math.PI * 2 : (Math.random() * 2 - 1) * ev.half * 0.9), r0 = 0.7 + Math.random() * 0.9;
      Object.assign(l, { on: true, x: ev.x + Math.cos(a) * r0, y: gy + 0.5 + Math.random() * 1.4, z: ev.z + Math.sin(a) * r0, dx: Math.cos(a), dz: Math.sin(a), len: 1 + Math.random() * 1.4, sp: ev.speed * (0.55 + Math.random() * 0.35), t: -Math.random() * 0.05 });
    }
    // the shout at the hero: a tight shock ring and a short white pop at chest height
    fx.rings.add(ev.x, gy + 0.15, ev.z, 2.2, 0.22, PALE, 2.2, 1.2);
    if (!ev.echo) fx.stars.add(ev.x + Math.cos(s.a) * 0.6, gy + 1.45, ev.z + Math.sin(s.a) * 0.6, 1.2, 0.08, 0xffffff, 2.2);
    glow.flash(ev.x + Math.cos(s.a) * 1.5, ev.z + Math.sin(s.a) * 1.5, 2.2, 0xbfdcff, 0.35, 0.18);
    fx.shake = Math.min(1, fx.shake + (ev.echo ? 0.1 : 0.18));
  }

  update(dt: number, cam: THREE.Camera, fx: Fx): void {
    this.poseT = Math.max(0, this.poseT - dt);
    this.pose += ((this.poseT > 0 ? 1 : 0) - this.pose) * Math.min(1, dt * (this.poseT > 0 ? 20 : 7));
    for (const s of this.slots) {
      if (!s.on) { s.wall.visible = false; s.ground.visible = false; continue; }
      s.t = this.hold ?? s.t + dt;
      const tEnd = s.r / s.speed, front = Math.min(s.r, s.speed * Math.max(0, s.t)), fade = s.t > tEnd ? 1 - (s.t - tEnd) / FADE : 1;
      if (fade <= 0) { s.on = false; s.wall.visible = false; s.ground.visible = false; continue; }
      const a = Math.min(1, s.t / 0.04 + 0.2) * fade;
      for (const m of [s.wall, s.ground]) m.visible = true;
      s.wall.position.set(s.x, s.gy - 0.1, s.z); s.wall.scale.set(Math.max(0.3, front), WALL_H, Math.max(0.3, front));
      s.ground.position.set(s.x, s.gy + 0.06, s.z); const gr = front + 0.6; s.ground.scale.set(gr, 1, gr);
      // the cylinder's local angle runs from +x toward +z (atan(z, x)) like the sim's aim; scale doesn't change it
      s.u.aim.value = s.a; s.u.half.value = s.half; s.u.alpha.value = a; s.u.front.value = front; s.u.R.value = gr; (s.u.time.value as number) += dt;
      // dust skirt and pebbles kicked up along the front
      if (this.hold === null && front > 0.8 && fade > 0.5) {
        const full = s.half >= Math.PI - 1e-3, n = full ? 6 : 3;
        for (let k = 0; k < n; k++) {
          const an = s.a + (full ? Math.random() * Math.PI * 2 : (Math.random() * 2 - 1) * s.half), px = s.x + Math.cos(an) * front, pz = s.z + Math.sin(an) * front;
          fx.blast?.dust(px, this.groundAt(px, pz) + 0.2, pz, Math.cos(an) * s.speed * 0.3, 0.4 + Math.random() * 0.6, Math.sin(an) * s.speed * 0.3, 0.45 + Math.random() * 0.3, 0.35, 1.1 + Math.random() * 0.6);
          if (Math.random() < 0.3) fx.particles.emitDir(px, this.groundAt(px, pz) + 0.15, pz, Math.cos(an) * 4, 2 + Math.random() * 3, Math.sin(an) * 4, DUST, 0.12, 0.4, 0.9);
        }
      }
    }
    // speed lines
    const R = this.ribbons, q = R.pts; R.begin();
    for (const l of this.lines) {
      if (!l.on) continue;
      l.t = this.hold !== null ? Math.min(this.hold, LINE_LIFE * 0.5) : l.t + dt;
      if (l.t > LINE_LIFE) { l.on = false; continue; }
      if (l.t < 0) continue;
      const d = l.sp * l.t, f = 1 - l.t / LINE_LIFE;
      for (let i = 0; i < 4; i++) { const u = i / 3, o = i * RS_STRIDE, s = d + l.len * u; q[o] = l.x + l.dx * s; q[o + 1] = l.y; q[o + 2] = l.z + l.dz * s; q[o + 3] = 0.07 * Math.sin(Math.PI * u) * (0.5 + f); q[o + 4] = f; }
      R.commit(4, cam.position, _edge, _core, 0.5);
    }
    R.end();
  }

  /** Hands-forward push for the procedural rig: the approved Nova Lance release key (same reuse as Flare Barrage). */
  poseRig(rig: HeroRig): void {
    const w = this.pose; if (w <= 0.01) return;
    const k = LANCE_POSES[ACTIVE_LANCE].thrust, a = rig.armR.rotation, b = rig.armL.rotation;
    a.set(a.x + (k.armR[0] - a.x) * w, a.y + (k.armR[1] - a.y) * w, a.z + (k.armR[2] - a.z) * w);
    b.set(b.x + (k.armL[0] - b.x) * w, b.y + (k.armL[1] - b.y) * w, b.z + (k.armL[2] - b.z) * w);
    rig.foreR.rotation.x += (k.foreR - rig.foreR.rotation.x) * w; rig.foreL.rotation.x += (k.foreL - rig.foreL.rotation.x) * w;
  }

  clear(): void { for (const s of this.slots) { s.on = false; s.wall.visible = false; s.ground.visible = false; } for (const l of this.lines) l.on = false; this.ribbons.clear(); this.pose = 0; this.poseT = 0; }
}
